import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  Grid,
  Rating,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiService } from '../services/api.js';
import GoogleLoginButton from '../components/GoogleLoginButton.jsx';

const SummaryPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const {
    vehicleRegistration,
    vehicleData,
    selectedGarage,
    selectedSlot,
    availableSlots,
    postcode,
    selectedProducts,
    selectedAddons,
    garageSelectedProducts,
    garageSelectedAddons,
    setGarageSelectedProducts,
    setGarageSelectedAddons,
    totalAmount,
    setBookingData,
    setLoading,
    setError,
    clearError,
    isLoading,
    error
  } = useBooking();

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [redirectDelay, setRedirectDelay] = useState(true);
  const [loadingGarageData, setLoadingGarageData] = useState(false);

  // Memoize whether we have complete booking data
  const hasCompleteBookingData = useMemo(() => {
    return !!(vehicleRegistration &&
      vehicleData &&
      selectedGarage &&
      selectedSlot &&
      selectedSlot.time &&
      selectedSlot.slot_id &&
      selectedSlot.calculatedAmount !== null);
  }, [vehicleRegistration, vehicleData, selectedGarage, selectedSlot]);

  // Add a small delay before checking for redirects to avoid issues during login
  useEffect(() => {
    const timer = setTimeout(() => {
      setRedirectDelay(false);
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only redirect after the delay period to avoid issues during login
    // Also don't redirect if user is in the middle of authentication
    if (!redirectDelay && !hasCompleteBookingData && !authLoading) {
      navigate('/');
      return;
    }
  }, [redirectDelay, hasCompleteBookingData, navigate, authLoading]);

  // Load garage-specific service data if not available
  useEffect(() => {
    const loadGarageServiceData = async () => {
      if (!selectedGarage?.id || !selectedProducts?.length || loadingGarageData) {
        return;
      }
      
      // If we don't have garage-specific data, load it
      if ((!garageSelectedProducts || garageSelectedProducts.length === 0) && 
          (!garageSelectedAddons || garageSelectedAddons.length === 0)) {
        
        setLoadingGarageData(true);
        clearError();
        
        try {
          // Get today's date for availability call
          const today = new Date();
          const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          const endDate = startDate; // Just check today
          
          const productIds = selectedProducts.map(p => p.product_id);
          const addonIds = selectedAddons.map(a => a.addon_id);
          
          const response = await apiService.post(
            `/garages/${selectedGarage.id}/availability`,
            {
              start_date: startDate,
              end_date: endDate,
              product_ids: productIds,
              addon_ids: addonIds
            }
          );
          
          const slotsData = response.data;
          
          // Update garage-specific service data
          if (slotsData.selected_products) {
            setGarageSelectedProducts(slotsData.selected_products);
          }
          if (slotsData.selected_addons) {
            setGarageSelectedAddons(slotsData.selected_addons);
          }
          
        } catch (error) {
          console.error('Error loading garage service data:', error);
          setError('Failed to load service pricing information');
        } finally {
          setLoadingGarageData(false);
        }
      }
    };
    
    loadGarageServiceData();
  }, [selectedGarage?.id, selectedProducts, selectedAddons, garageSelectedProducts, garageSelectedAddons, loadingGarageData]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateForAPI = (date) => {
    try {
      // Handle different date formats
      let dateObj;
      if (typeof date === 'string') {
        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        // Handle toDateString() format like "Mon Jul 22 2025"
        dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          throw new Error('Invalid date string');
        }
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        // Fallback to current date
        dateObj = new Date();
      }
      
      // Validate the date object
      if (isNaN(dateObj.getTime())) {
        throw new Error('Invalid date object');
      }
      
      // Format as YYYY-MM-DD
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for API:', error, 'Input date:', date);
      // Return today's date as fallback
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }
  };

  const formatTimeForAPI = (time) => {
    try {
      // If already in HH:MM format, return as is
      if (/^\d{2}:\d{2}$/.test(time)) {
        return time;
      }
      
      // If it's in HH:MM:SS format, extract HH:MM
      if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
        return time.substring(0, 5);
      }
      
      // Try to parse and format
      const timeObj = new Date(`2000-01-01T${time}`);
      if (!isNaN(timeObj.getTime())) {
        const hours = String(timeObj.getHours()).padStart(2, '0');
        const minutes = String(timeObj.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      
      // Fallback - return the original time
      return time;
    } catch (error) {
      console.error('Error formatting time for API:', error, time);
      return time; // Return original time as fallback
    }
  };

  const calculateSubtotal = () => {
    // Use the calculated amount from the selected slot first, but only if it's a valid non-zero value
    if (selectedSlot && selectedSlot.calculatedAmount !== null && selectedSlot.calculatedAmount !== undefined && !isNaN(selectedSlot.calculatedAmount) && selectedSlot.calculatedAmount > 0) {
      return selectedSlot.calculatedAmount;
    }

    // Calculate from garage-specific service data
    let total = 0;
    
    // Add product prices
    if (garageSelectedProducts && garageSelectedProducts.length > 0) {
      total += garageSelectedProducts.reduce((sum, product) => {
        const price = parseFloat(product.garage_price) || 0;
        return sum + price;
      }, 0);
    }
    
    // Add addon prices
    if (garageSelectedAddons && garageSelectedAddons.length > 0) {
      total += garageSelectedAddons.reduce((sum, addon) => {
        const price = parseFloat(addon.garage_price) || 0;
        return sum + price;
      }, 0);
    }
    
    // Apply slot price multiplier if available
    if (selectedSlot && selectedSlot.price_multiplier && selectedSlot.price_multiplier > 0) {
      total = total * selectedSlot.price_multiplier;
    }
    
    return total;
  };

  // Calculate 2% online fee
  const calculateOnlineFee = () => {
    const subtotal = calculateSubtotal();
    return subtotal * 0.02; // 2% fee
  };

  // Calculate total including online fee
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const onlineFee = calculateOnlineFee();
    return subtotal + onlineFee;
  };

  const handleProceedToCheckout = async () => {
    // If not authenticated, redirect to sign-in flow
    if (!isAuthenticated) {
      // This will be handled by GoogleLoginButton redirect logic
      setError('Please sign in to continue with checkout');
      return;
    }

    // Check if we have an auth token
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      setError('Authentication token missing. Please sign in again.');
      return;
    }

    setPaymentLoading(true);
    clearError();

    try {
      // Use vehicle ID from context (already fetched during initial lookup)
      if (!vehicleData?.id) {
        throw new Error('Vehicle data not available. Please start over.');
      }

      // Prepare products and addons data from garage-specific data
      const products = (garageSelectedProducts || selectedProducts || []).map(product => ({
        product_id: product.id || product.product_id,
        price: Math.round((product.garage_price || product.base_price || 0) * 100) // Convert to cents
      }));
      
      const addons = (garageSelectedAddons || selectedAddons || []).map(addon => ({
        addon_id: addon.id || addon.addon_id,
        price: Math.round((addon.garage_price || addon.base_price || 0) * 100) // Convert to cents
      }));
      
      // Calculate total duration from garage-specific services
      let totalDuration = 0;
      if (garageSelectedProducts && garageSelectedProducts.length > 0) {
        totalDuration += garageSelectedProducts.reduce((sum, product) => sum + (product.duration_minutes || 0), 0);
      }
      if (garageSelectedAddons && garageSelectedAddons.length > 0) {
        totalDuration += garageSelectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
      }
      
      // Calculate amounts in cents
      const subtotalCents = Math.round(calculateSubtotal() * 100);
      const onlineFeeCents = Math.round(calculateOnlineFee() * 100);
      const totalAmountCents = Math.round(calculateTotal() * 100);
      
      
      const formattedDate = formatDateForAPI(selectedSlot.date);
      const formattedTime = formatTimeForAPI(selectedSlot.time);
      
      
      // Create checkout session with new structure including online fee
      const checkoutData = {
        vehicle_id: vehicleData.id,
        garage_id: selectedGarage.id,
        booking_date: formattedDate,
        booking_time: formattedTime,
        duration_minutes: totalDuration || 60, // Fallback to 60 if no duration calculated
        products: products,
        addons: addons,
        subtotal: subtotalCents,
        online_fee: onlineFeeCents,
        total_amount: totalAmountCents,
        // Add line items for Stripe checkout display
        line_items: [
          ...(garageSelectedProducts || selectedProducts || []).map(product => ({
            name: product.name || 'Service',
            amount: Math.round((product.garage_price || product.base_price || 0) * 100),
            quantity: 1
          })),
          ...(garageSelectedAddons || selectedAddons || []).map(addon => ({
            name: addon.name || 'Add-on',
            amount: Math.round((addon.garage_price || addon.base_price || 0) * 100),
            quantity: 1
          })),
          {
            name: 'Online booking fee (2%)',
            amount: onlineFeeCents,
            quantity: 1
          }
        ]
      };
      

      const response = await apiService.createCheckoutSession(checkoutData);

      if (response.data.session_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.session_url;
      } else {
        throw new Error('No payment session URL received');
      }

    } catch (error) {
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{ cursor: 'pointer' }}
        >
          {t('nav.home')}
        </Link>
        <Link
          color="inherit"
          href="/garages"
          onClick={(e) => { e.preventDefault(); navigate('/garages'); }}
          sx={{ cursor: 'pointer' }}
        >
          Find Garages
        </Link>
        <Link
          color="inherit"
          href="/appointment"
          onClick={(e) => { e.preventDefault(); navigate('/appointment'); }}
          sx={{ cursor: 'pointer' }}
        >
          {t('nav.appointment')}
        </Link>
        <Typography color="text.primary">{t('summary.title')}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('summary.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please review your booking details before proceeding to payment
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Clean Summary Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Booking Details */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 4, color: 'primary.main' }}>
                Booking Summary
              </Typography>

              {/* Vehicle Registration */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CarIcon color="primary" sx={{ fontSize: 24 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Vehicle
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" sx={{ ml: 4, color: 'text.primary' }}>
                  {vehicleRegistration}
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Garage */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <LocationIcon color="primary" sx={{ fontSize: 24 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Garage
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    {selectedGarage?.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedGarage?.address}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedGarage?.postcode}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Appointment Date & Time */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <ScheduleIcon color="primary" sx={{ fontSize: 24 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Appointment
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatDate(selectedSlot?.date)}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatTime(selectedSlot?.time)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Payment Summary */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Payment Summary Card */}
          <Card elevation={3} sx={{ position: 'sticky', top: 20, mb: 3 }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                Payment Summary
              </Typography>

              {/* Selected Services List */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Selected Services
                </Typography>
                
                {/* Products */}
                {garageSelectedProducts && garageSelectedProducts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {garageSelectedProducts.map((product, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: index < garageSelectedProducts.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {product.name || 'Service'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.duration_minutes || 0} mins
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          £{(product.garage_price || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Addons */}
                {garageSelectedAddons && garageSelectedAddons.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {garageSelectedAddons.map((addon, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: index < garageSelectedAddons.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {addon.name || 'Add-on'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {addon.duration_minutes || 0} mins
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color={addon.garage_price === 0 ? 'success.main' : 'text.primary'}>
                          {addon.garage_price === 0 ? 'Free' : `£${(addon.garage_price || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Subtotal */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography variant="body1" fontWeight="500">
                    Subtotal
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    £{calculateSubtotal().toFixed(2)}
                  </Typography>
                </Box>

                {/* Online Fee */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Online booking fee (2%)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    £{calculateOnlineFee().toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Total */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: { xs: 2, sm: 3 }, 
                  backgroundColor: 'primary.50', 
                  borderRadius: 2 
                }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total Amount
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ 
                    textAlign: 'right',
                    fontSize: { xs: '1.75rem', sm: '2.125rem' }
                  }}>
                    £{calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Payment Action Section */}
              {isAuthenticated ? (
                <>
                  {/* Continue to Payment Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleProceedToCheckout}
                      disabled={paymentLoading}
                      sx={{
                        px: { xs: 3, sm: 4 },
                        py: { xs: 1, sm: 1.5 },
                        minWidth: { xs: '100%', sm: 200 },
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        maxWidth: { xs: '100%', sm: 'none' },
                        '&.Mui-disabled': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          opacity: 0.8
                        }
                      }}

                    >
                      {paymentLoading ? <CircularProgress size={20} color="inherit" /> : ' Continue to payment'}
                    
                    </Button>
                  </Box>

                  {/* Security notice */}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    🔒 Secure payment powered by Stripe
                  </Typography>
                </>
              ) : (
                <>
                  {/* Google Sign-in Button for non-authenticated users */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <GoogleLoginButton />
                  </Box>

                  {/* Security notice */}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    🔒 Secure payment powered by Stripe
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Terms and Conditions */}
      <Box sx={{ mt: 4, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          By proceeding to payment, you agree to our{' '}
          <Link href="/terms" target="_blank" rel="noopener noreferrer">
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default SummaryPage;